import { useRef, useState } from 'react';

import type { BoardDetailOut } from '@solar/api-client';
import { ApiError } from '@solar/api-client';
import { ErrorBox, Spinner, formatDateTime, useAsync } from '@solar/ui';

import { api } from '../api';
import { Modal } from '../Modal';

/**
 * 게시판(공지사항/자료실/직원게시판) — 레거시 board/{notice,data,member} 파리티.
 *
 * 세 게시판은 목록/상세/첨부 구조가 같고 권한만 다르다:
 * - notice: 조회 전 직원, 작성/수정/삭제 에너지공사(1,2) — 버튼도 그 역할에만 보인다.
 * - data/member: 같은 회사 직원 누구나 쓴다(회사 격리는 서버 강제).
 * - member 만 댓글(bd_member_comment)이 붙는다.
 */

export type StaffBoardKind = 'notice' | 'data' | 'member';

const BOARD_TITLE: Record<StaffBoardKind, string> = {
  notice: '공지사항',
  data: '자료실',
  member: '직원게시판',
};

/** notice/faq 작성 권한 — 서버 _ENERGY_ROLES(1,2)와 동일. */
const ENERGY_ROLES = [1, 2];

const SIZE = 30;

export function BoardManage({ kind, role }: { kind: StaffBoardKind; role: number }) {
  const [keyword, setKeyword] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [applied, setApplied] = useState({ keyword: '', dateFrom: '', dateTo: '' });
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  // 레거시 접근제어는 DB permission 테이블이라 정적 재현이 불가 — 서버 규칙과
  // 동일한 구조적 근거(전사 게시판 작성=에너지공사)로 버튼만 걸러 보여준다.
  const canWrite = kind === 'notice' ? ENERGY_ROLES.includes(role) : true;

  const list = useAsync(
    () =>
      api.listBoards(kind, {
        keyword: applied.keyword || undefined,
        date_from: applied.dateFrom || undefined,
        date_to: applied.dateTo || undefined,
        page,
        size: SIZE,
      }),
    [applied, page],
  );

  const total = list.data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / SIZE));

  return (
    <div className="admin__body">
      <section className="admin__list">
        <div className="toolbar">
          <form
            className="search"
            onSubmit={(e) => {
              e.preventDefault();
              setApplied({ keyword: keyword.trim(), dateFrom, dateTo });
              setPage(1);
            }}
          >
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="제목 또는 내용"
            />
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              aria-label="작성일 시작"
            />
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              aria-label="작성일 종료"
            />
            <button type="submit">검색</button>
          </form>
          {canWrite && (
            <button type="button" className="primary create" onClick={() => setCreateOpen(true)}>
              등록하기
            </button>
          )}
        </div>

        {list.loading && <Spinner />}
        {list.error && <ErrorBox message={list.error} onRetry={list.reload} />}

        {list.data && (
          <table className="grid">
            <thead>
              <tr>
                <th>No</th>
                <th>제목</th>
                <th>글쓴이</th>
                <th className="num">조회수</th>
                <th>등록일시</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted">
                    등록된 글이 없습니다.
                  </td>
                </tr>
              )}
              {list.data.items.map((b) => (
                <tr
                  key={b.id}
                  className={b.id === selected ? 'selected' : ''}
                  onClick={() => setSelected(b.id)}
                >
                  <td>{b.id}</td>
                  <td>{b.title}</td>
                  <td>{b.author_name ?? '-'}</td>
                  <td className="num">{b.view_cnt ?? '-'}</td>
                  <td>{formatDateTime(b.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="pager">
          <button type="button" onClick={() => setPage((p) => p - 1)} disabled={page <= 1}>
            이전
          </button>
          <span>
            {page} / {lastPage} (총 {total}건)
          </span>
          <button type="button" onClick={() => setPage((p) => p + 1)} disabled={page >= lastPage}>
            다음
          </button>
        </div>
      </section>

      <aside className="admin__aside">
        {selected === null ? (
          <p className="muted">글을 선택하세요.</p>
        ) : (
          <BoardPostDetail
            key={selected}
            kind={kind}
            postId={selected}
            canWrite={canWrite}
            onClose={() => setSelected(null)}
            onChanged={() => list.reload()}
            onDeleted={() => {
              setSelected(null);
              list.reload();
            }}
          />
        )}
      </aside>

      {createOpen && (
        <BoardCreateModal
          kind={kind}
          onClose={() => setCreateOpen(false)}
          onCreated={(id) => {
            // 첨부는 글이 있어야 presign 이 되므로, 등록 직후 상세를 열어 잇게 한다.
            setCreateOpen(false);
            setSelected(id);
            list.reload();
          }}
        />
      )}
    </div>
  );
}

function BoardPostDetail({
  kind,
  postId,
  canWrite,
  onClose,
  onChanged,
  onDeleted,
}: {
  kind: StaffBoardKind;
  postId: number;
  canWrite: boolean;
  onClose: () => void;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const detail = useAsync(() => api.getBoard(kind, postId), [kind, postId]);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await api.deleteBoard(kind, postId);
      onDeleted();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '삭제하지 못했습니다.');
      setConfirmDelete(false);
    } finally {
      setBusy(false);
    }
  }

  if (detail.loading) return <Spinner />;
  if (detail.error) return <ErrorBox message={detail.error} onRetry={detail.reload} />;
  const post = detail.data;
  if (!post) return null;

  if (editing) {
    return (
      <BoardEditForm
        kind={kind}
        post={post}
        onCancel={() => setEditing(false)}
        onSaved={() => {
          setEditing(false);
          detail.reload();
          onChanged();
        }}
      />
    );
  }

  return (
    <div className="detail">
      <div className="detail__head">
        <h2>{post.title}</h2>
        <button type="button" className="link" onClick={onClose}>
          닫기
        </button>
      </div>

      <section className="panel">
        <div className="field">
          <span className="field__label">글쓴이</span>
          <span className="field__value">{post.author_name ?? '-'}</span>
        </div>
        <div className="field">
          <span className="field__label">등록일시</span>
          <span className="field__value">{formatDateTime(post.created_at)}</span>
        </div>
        <div className="field">
          <span className="field__label">조회수</span>
          <span className="field__value">{post.view_cnt ?? '-'}</span>
        </div>
      </section>

      <h3 className="section">내용</h3>
      <section className="panel">
        <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{post.contents ?? '-'}</p>
      </section>

      <BoardFiles
        kind={kind}
        postId={postId}
        files={post.files}
        canWrite={canWrite}
        onChanged={() => detail.reload()}
      />

      {kind === 'member' && (
        <BoardComments postId={postId} comments={post.comments} onChanged={() => detail.reload()} />
      )}

      {error && <p className="error">{error}</p>}

      {canWrite && (
        <div className="row form-actions">
          <button type="button" disabled={busy} onClick={() => setConfirmDelete(true)}>
            삭제
          </button>
          <button type="button" className="primary" disabled={busy} onClick={() => setEditing(true)}>
            수정
          </button>
        </div>
      )}

      {confirmDelete && (
        <Modal title={`${BOARD_TITLE[kind]} 삭제`} onClose={() => setConfirmDelete(false)}>
          <p>
            <strong>{post.title}</strong> 글을 삭제합니다. 계속할까요?
          </p>
          <div className="row">
            <button type="button" onClick={() => setConfirmDelete(false)}>
              취소
            </button>
            <button type="button" className="primary" disabled={busy} onClick={() => void remove()}>
              삭제
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function BoardEditForm({
  kind,
  post,
  onCancel,
  onSaved,
}: {
  kind: StaffBoardKind;
  post: BoardDetailOut;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(post.title);
  const [contents, setContents] = useState(post.contents ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!title.trim() || !contents.trim()) {
      setError('제목과 내용을 입력해 주세요.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.updateBoard(kind, post.id, { title: title.trim(), contents });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="detail">
      <div className="detail__head">
        <h2>{BOARD_TITLE[kind]} 수정</h2>
        <button type="button" className="link" onClick={onCancel}>
          취소
        </button>
      </div>

      <section className="panel form-grid">
        <label className="f wide">
          제목 *
          <input value={title} maxLength={100} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label className="f wide">
          내용 *
          <textarea rows={10} value={contents} onChange={(e) => setContents(e.target.value)} />
        </label>
      </section>

      {error && <p className="error">{error}</p>}
      <div className="row form-actions">
        <button type="button" onClick={onCancel}>
          취소
        </button>
        <button type="button" className="primary" disabled={busy} onClick={() => void save()}>
          {busy ? '저장 중…' : '저장'}
        </button>
      </div>
    </div>
  );
}

/** 첨부파일 — presigned GET 다운로드 + presign 2단계 업로드(주문 사진과 동일 패턴). */
function BoardFiles({
  kind,
  postId,
  files,
  canWrite,
  onChanged,
}: {
  kind: StaffBoardKind;
  postId: number;
  files: BoardDetailOut['files'];
  canWrite: boolean;
  onChanged: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function upload(selected: FileList | null) {
    if (!selected || selected.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      for (const file of Array.from(selected)) {
        await api.uploadBoardFile(kind, postId, file);
      }
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '첨부파일을 업로드하지 못했습니다.');
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  }

  async function remove(fileId: number) {
    setBusy(true);
    setError(null);
    try {
      await api.deleteBoardFile(kind, postId, fileId);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '첨부파일을 삭제하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h3 className="section">첨부파일 ({files.length})</h3>
      <section className="panel">
        {files.length === 0 && <p className="muted">첨부파일이 없습니다.</p>}
        {files.map((f) => (
          <div key={f.id} className="row">
            {f.url ? (
              <a href={f.url} target="_blank" rel="noreferrer">
                {f.file_name}
              </a>
            ) : (
              <span>{f.file_name}</span>
            )}
            {canWrite && (
              <button type="button" className="link" disabled={busy} onClick={() => void remove(f.id)}>
                삭제
              </button>
            )}
          </div>
        ))}
        {canWrite && (
          <div className="row">
            <input
              ref={input}
              type="file"
              multiple
              disabled={busy}
              onChange={(e) => void upload(e.target.files)}
            />
            {busy && <span className="muted">업로드 중…</span>}
          </div>
        )}
        {error && <p className="error">{error}</p>}
      </section>
    </>
  );
}

/** 직원게시판 댓글 — 부모 글 접근 가능 = 등록/삭제 가능(레거시·서버 동일 규칙). */
function BoardComments({
  postId,
  comments,
  onChanged,
}: {
  postId: number;
  comments: BoardDetailOut['comments'];
  onChanged: () => void;
}) {
  const [contents, setContents] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function add() {
    if (!contents.trim()) {
      setError('댓글 내용을 입력해 주세요.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.addBoardComment(postId, contents.trim());
      setContents('');
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '댓글을 등록하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(commentId: number) {
    setBusy(true);
    setError(null);
    try {
      await api.deleteBoardComment(postId, commentId);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '댓글을 삭제하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h3 className="section">댓글 ({comments.length})</h3>
      <section className="panel">
        {comments.length === 0 ? (
          <p className="muted">댓글이 없습니다.</p>
        ) : (
          <ul className="memos">
            {comments.map((c) => (
              <li key={c.id}>
                <p>{c.contents}</p>
                <span className="muted">
                  {c.author_name ?? '-'} · {formatDateTime(c.created_at)}{' '}
                  <button
                    type="button"
                    className="link"
                    disabled={busy}
                    onClick={() => void remove(c.id)}
                  >
                    삭제
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
        <form
          className="row"
          onSubmit={(e) => {
            e.preventDefault();
            void add();
          }}
        >
          <input
            value={contents}
            onChange={(e) => setContents(e.target.value)}
            placeholder="댓글을 입력해 주세요."
          />
          <button type="submit" className="primary" disabled={busy}>
            등록
          </button>
        </form>
        {error && <p className="error">{error}</p>}
      </section>
    </>
  );
}

function BoardCreateModal({
  kind,
  onClose,
  onCreated,
}: {
  kind: StaffBoardKind;
  onClose: () => void;
  onCreated: (id: number) => void;
}) {
  const [title, setTitle] = useState('');
  const [contents, setContents] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!title.trim() || !contents.trim()) {
      setError('제목과 내용을 입력해 주세요.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = await api.createBoard(kind, { title: title.trim(), contents });
      onCreated(created.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '등록하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={`${BOARD_TITLE[kind]} 등록`} onClose={onClose}>
      <label className="labeled">
        제목 *
        <input value={title} maxLength={100} onChange={(e) => setTitle(e.target.value)} />
      </label>
      <label className="labeled">
        내용 *
        <textarea rows={8} value={contents} onChange={(e) => setContents(e.target.value)} />
      </label>
      <p className="muted">첨부파일은 등록 후 상세 화면에서 올릴 수 있습니다.</p>

      {error && <p className="error">{error}</p>}
      <div className="row">
        <button type="button" onClick={onClose}>
          취소
        </button>
        <button type="button" className="primary" disabled={busy} onClick={() => void submit()}>
          {busy ? '등록 중…' : '등록'}
        </button>
      </div>
    </Modal>
  );
}
